import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Slider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { tokens } from "../theme";
import { foyerApi, type SignageUrlProbe } from "../http/foyer";
import { ImageListControl, withPicked } from "./SignageSlideDialog";
import { ELFSIGHT_VIEW_NOTE, elfsightShareLink } from "@shared/elfsight";
import AssetsPicker from "./AssetsPicker";
import TemplateSlide from "../../tv/signage/TemplateSlide";
import { FONT_CATALOGUE } from "@shared/schemes/fonts";
import {
  BLANK_PRESET_ID,
  DEFAULT_THEME,
  SIGNAGE_WIDGETS,
  TEMPLATE_PRESETS,
  applyTheme,
  canAddWebRegion,
  fieldsFor,
  newWidgetRegion,
  styleForRole,
  type SignageWidgetId,
} from "@shared/signage-template-presets";
import {
  MAX_WEB_REGIONS,
  type SignageRegionRole,
  type SignageTemplate,
  type SignageTemplateRegion,
  type SignageTemplateTheme,
} from "@shared/types/signage-template";

/**
 * Design a signage template, in three steps.
 *
 * ## What this replaced, and why
 *
 * The first version was a blank canvas plus an inspector of raw model fields -
 * x, y, width, height, font weight, letter spacing, vertical align - and a
 * separate list where you declared "fields" with types and keys, then bound
 * regions to them through a dropdown. Every control mapped to something real.
 * It was still unusable: *"I don't understand anything you have built. There
 * are too many weird input fields and buttons."*
 *
 * The lesson is the ordinary one and worth writing down because it survived
 * review: **exposing the data model as a form is not a user interface.** A
 * hotel's content manager does not think in rectangles, and every decision this
 * editor asked them to make was one the product could have made for them.
 *
 * ## The three steps, and what each one removes
 *
 * 1. **Pick a layout.** A template starts as a finished design that already
 *    looks right. This removes placement, sizing, and the empty canvas nobody
 *    knew how to fill.
 * 2. **Colours and typeface.** Four controls for the whole template, applied
 *    to every block by its ROLE. This removes eleven style controls per region
 *    and, more importantly, removes the possibility of a design that looks
 *    wrong.
 * 3. **Say what changes.** Click a block: same every time, or filled in per
 *    slide. This removes the separate field list entirely - a field IS a block
 *    somebody marked - and with it the two silent failures the old shape had
 *    (a field nothing drew, and a region orphaned by a renamed key).
 *
 * Dragging a block still works, in step 3, for the case where the preset is
 * nearly right. It is not needed to get a good result, which is the point.
 *
 * ## "Make my own" (2026-09-23)
 *
 * The first card in step 1 is an EMPTY canvas, and step 3 carries a bar of
 * widgets - Heading, Image, Webpage, Instagram, VilMer, List... - that drop a
 * finished, theme-styled block onto it. Blocks move and resize by dragging,
 * snapped to a 20px grid so things line up without anybody measuring.
 *
 * This is NOT the inspector that was thrown out. Nothing asks for a
 * coordinate or a font size: a block's look is still its role under the
 * theme, and the most a block offers is "heading / text / small print" and an
 * alignment. Placement is the hand's job; styling stays the product's.
 */

type StepId = 0 | 1 | 2;
const STEPS = ["Pick a layout", "Make it yours", "Build it"];

/** Everything snaps to this, in canvas pixels. */
const GRID = 20;
const snap = (n: number) => Math.round(n / GRID) * GRID;

export interface SignageTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Absent = designing a new one. */
  template?: SignageTemplate | null;
  onSave: (body: Partial<SignageTemplate>) => Promise<void>;
  saving?: boolean;
  error?: string | null;
}

export default function SignageTemplateDialog({
  open,
  onClose,
  template,
  onSave,
  saving,
  error,
}: SignageTemplateDialogProps) {
  const [step, setStep] = useState<StepId>(0);
  const [name, setName] = useState("");
  const [canvas, setCanvas] = useState({ width: 1920, height: 1080 });
  const [theme, setTheme] = useState<SignageTemplateTheme>(DEFAULT_THEME);
  const [regions, setRegions] = useState<SignageTemplateRegion[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [picking, setPicking] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(template?.name ?? "");
    setCanvas(template?.canvas ?? { width: 1920, height: 1080 });
    setTheme(template?.theme ?? DEFAULT_THEME);
    setRegions(template?.regions ?? []);
    setLabels(
      Object.fromEntries((template?.fields ?? []).map((f) => [f.key, f.label]))
    );
    setSelected(null);
    // An existing template opens at the last step: its layout was chosen long
    // ago, and somebody reopening it is almost always changing the words.
    setStep(template ? 2 : 0);
  }, [open, template]);

  const choosePreset = (presetId: string) => {
    if (presetId === BLANK_PRESET_ID) {
      // Straight to the building step: there is nothing on the canvas yet for
      // colours to be judged against, and Back is one click away.
      setCanvas({ width: 1920, height: 1080 });
      setRegions([]);
      setLabels({});
      if (!name.trim()) setName("My template");
      setStep(2);
      return;
    }
    const preset = TEMPLATE_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    setCanvas(preset.canvas);
    setRegions(applyTheme(preset.regions, theme));
    setLabels({});
    if (!name.trim()) setName(preset.name);
    setStep(1);
  };

  const changeTheme = (change: Partial<SignageTemplateTheme>) => {
    const next = { ...theme, ...change };
    setTheme(next);
    // Re-styled immediately, so step 2 shows the effect rather than promising
    // it. A colour picker whose result appears after Save is a colour picker
    // nobody trusts.
    setRegions((prev) => applyTheme(prev, next));
  };

  const addWidget = (widget: SignageWidgetId) => {
    const id = `${widget}-${Math.random().toString(36).slice(2, 8)}`;
    setRegions((prev) => [...prev, newWidgetRegion(widget, canvas, theme, id)]);
    setSelected(id);
  };

  const removeRegion = (id: string) => {
    setRegions((prev) => prev.filter((r) => r.id !== id));
    setSelected(null);
  };

  /** Front = drawn last. The array order IS the stacking order. */
  const restack = (id: string, to: "front" | "back") =>
    setRegions((prev) => {
      const region = prev.find((r) => r.id === id);
      if (!region) return prev;
      const rest = prev.filter((r) => r.id !== id);
      return to === "front" ? [...rest, region] : [region, ...rest];
    });

  const preview = useMemo(
    () => ({
      instanceId: "preview",
      templateId: template?.id ?? "new",
      version: template?.version ?? 1,
      canvas,
      background: theme.background,
      regions,
      // Sample words, so step 2 is judged on a design rather than on emptiness.
      values: Object.fromEntries(
        regions
          .filter((r) => r.binding.kind === "field")
          .map((r) => {
            const key = (r.binding as { field: string }).field;
            return [
              key,
              r.kind === "image" || r.kind === "web" || r.kind === "slideshow"
                ? ""
                : r.kind === "qr"
                  ? "https://example.com"
                  : r.kind === "list"
                    ? SAMPLE_ROWS
                    : (labels[key] ?? sampleFor(r)),
            ];
          })
      ),
    }),
    [canvas, theme, regions, labels, template]
  );

  const canSave = name.trim().length > 0 && regions.length > 0 && !saving;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontSize: 16, pb: 0 }}>
        {template ? "Edit template" : "New template"}
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={step} sx={{ my: 2 }}>
          {STEPS.map((label, i) => (
            <Step key={label} completed={step > i}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && <PickLayout onChoose={choosePreset} />}

        {step > 0 && (
          <Stack direction={{ xs: "column", md: "row" }} gap={3}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {step === 2 && (
                <WidgetBar
                  canAddWeb={canAddWebRegion(regions)}
                  onAdd={addWidget}
                />
              )}
              <Canvas
                canvas={canvas}
                theme={theme}
                regions={regions}
                preview={preview}
                selected={selected}
                interactive={step === 2}
                onSelect={setSelected}
                onMove={(id, rect) =>
                  setRegions((prev) =>
                    prev.map((r) => (r.id === id ? { ...r, rect } : r))
                  )
                }
                onDelete={removeRegion}
              />
              {step === 2 && (
                <Typography
                  sx={{ mt: 1, fontSize: 12, color: tokens.inkMuted }}
                >
                  Add blocks from the bar above. Click one to say what it does;
                  drag it to move it, and its corner to resize it.
                </Typography>
              )}
            </Box>

            <Stack
              gap={2}
              sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0 }}
            >
              <TextField
                size="small"
                label="Template name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{ "data-testid": "template-name" }}
              />

              {step === 1 ? (
                <MakeItYours theme={theme} onChange={changeTheme} />
              ) : (
                <BlockSettings
                  region={regions.find((r) => r.id === selected) ?? null}
                  label={
                    labels[
                      (
                        regions.find((r) => r.id === selected)?.binding as
                          | { field?: string }
                          | undefined
                      )?.field ?? ""
                    ] ?? ""
                  }
                  onChange={(next) =>
                    setRegions((prev) =>
                      prev.map((r) => (r.id === next.id ? next : r))
                    )
                  }
                  onLabel={(field, value) =>
                    setLabels((prev) => ({ ...prev, [field]: value }))
                  }
                  onPick={() => selected && setPicking(selected)}
                  theme={theme}
                  onDelete={removeRegion}
                  onRestack={restack}
                />
              )}
            </Stack>
          </Stack>
        )}

        {error && (
          <Typography sx={{ mt: 2, fontSize: 13, color: tokens.error }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        {step > 0 && (
          <Button onClick={() => setStep((s) => (s - 1) as StepId)}>
            Back
          </Button>
        )}
        {step === 1 && (
          <Button variant="contained" onClick={() => setStep(2)}>
            Next
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained"
            disabled={!canSave}
            onClick={() =>
              void onSave({
                name: name.trim(),
                canvas,
                background: theme.background,
                theme,
                regions,
                // DERIVED. There is no field list to keep in step, which is why
                // a field cannot exist without a block that draws it.
                fields: fieldsFor(regions, labels),
              })
            }
          >
            {template ? "Save template" : "Create template"}
          </Button>
        )}
      </DialogActions>

      <AssetsPicker
        open={picking !== null}
        onClose={() => setPicking(null)}
        onSelect={(url) => {
          if (picking)
            setRegions((prev) =>
              prev.map((r) =>
                r.id === picking
                  ? {
                      ...r,
                      binding: {
                        kind: "fixed",
                        // A rotation collects pictures; an image swaps its one.
                        value: withPicked(
                          r.kind === "slideshow" ? "images" : "image",
                          r.binding.kind === "fixed" ? r.binding.value : "",
                          url
                        ),
                      },
                    }
                  : r
              )
            );
          setPicking(null);
        }}
      />
    </Dialog>
  );
}

/**
 * Sample rows for a list block, so step 2 shows a timetable rather than a gap.
 *
 * Enough lines to see how the block behaves when it is full, which is the thing
 * a designer is actually deciding when they size it.
 */
const SAMPLE_ROWS = [
  "07:00 | Morning yoga",
  "09:30 | Spinning",
  "12:00 | Lunch is served",
  "17:30 | Body Pump",
  "Sauna open all day",
].join("\n");

/** Sample words so a design can be judged before anything is typed. */
function sampleFor(region: SignageTemplateRegion): string {
  if (region.role === "title") return "Your headline here";
  if (region.role === "small") return "Small print";
  return "A sentence about the thing you are announcing.";
}

/** Step 1: an empty canvas, then the finished designs, shown as designs. */
function PickLayout({ onChoose }: { onChoose: (id: string) => void }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-label="Make my own"
        onClick={() => onChoose(BLANK_PRESET_ID)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onChoose(BLANK_PRESET_ID);
          }
        }}
        sx={{
          border: `1px dashed ${tokens.brand}`,
          borderRadius: "10px",
          overflow: "hidden",
          cursor: "pointer",
          "&:hover": { background: tokens.brandTintSoft },
        }}
      >
        <Box
          sx={{
            aspectRatio: "16 / 9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 48,
            color: tokens.brand,
          }}
        >
          +
        </Box>
        <Box sx={{ p: 1.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            Make my own
          </Typography>
          <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
            Start empty and add pictures, text, web pages, Instagram…
          </Typography>
        </Box>
      </Box>
      {TEMPLATE_PRESETS.map((preset) => (
        <Box
          key={preset.id}
          role="button"
          tabIndex={0}
          aria-label={preset.name}
          onClick={() => onChoose(preset.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onChoose(preset.id);
            }
          }}
          sx={{
            border: `1px solid ${tokens.divider}`,
            borderRadius: "10px",
            overflow: "hidden",
            cursor: "pointer",
            "&:hover": { borderColor: tokens.brand },
          }}
        >
          <Box
            sx={{
              position: "relative",
              aspectRatio: "16 / 9",
              background: DEFAULT_THEME.background,
            }}
          >
            {/* The layout drawn as blocks rather than a stock photograph: it is
                the SHAPE of the message somebody is choosing between. */}
            {preset.regions.map((r) => (
              <Box
                key={r.id}
                sx={{
                  position: "absolute",
                  left: `${(r.rect.x / preset.canvas.width) * 100}%`,
                  top: `${(r.rect.y / preset.canvas.height) * 100}%`,
                  width: `${(r.rect.w / preset.canvas.width) * 100}%`,
                  height: `${(r.rect.h / preset.canvas.height) * 100}%`,
                  background:
                    r.kind === "image"
                      ? "rgba(255,255,255,0.16)"
                      : r.kind === "shape"
                        ? (r.style?.background ?? "rgba(255,255,255,0.3)")
                        : "transparent",
                  borderRadius: r.kind === "qr" ? "4px" : 0,
                  ...(r.kind === "qr"
                    ? { background: "rgba(255,255,255,0.85)" }
                    : {}),
                  ...(r.kind === "text" || r.kind === "clock"
                    ? {
                        borderTop: `${r.role === "title" ? 10 : 5}px solid rgba(255,255,255,0.75)`,
                      }
                    : {}),
                }}
              />
            ))}
          </Box>
          <Box sx={{ p: 1.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {preset.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
              {preset.description}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** Step 2: the whole of a template's look, in four controls. */
function MakeItYours({
  theme,
  onChange,
}: {
  theme: SignageTemplateTheme;
  onChange: (change: Partial<SignageTemplateTheme>) => void;
}) {
  return (
    <Stack gap={2}>
      <Colour
        label="Background"
        value={theme.background}
        onChange={(background) => onChange({ background })}
      />
      <Colour
        label="Text"
        value={theme.ink}
        onChange={(ink) => onChange({ ink })}
      />
      <Colour
        label="Highlight"
        value={theme.accent}
        onChange={(accent) => onChange({ accent })}
      />
      <TextField
        select
        size="small"
        label="Typeface"
        value={theme.font}
        onChange={(e) => onChange({ font: e.target.value })}
        // Already loaded by every screen, so anything chosen here appears with
        // no extra request and no flash of the wrong font on a panel.
        helperText="Already on every screen"
      >
        {FONT_CATALOGUE.map((f) => (
          <MenuItem key={f.stack} value={f.stack} sx={{ fontFamily: f.stack }}>
            {f.label}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}

/**
 * Step 3: what one block does.
 *
 * Two choices and, at most, one text box - plus, for words, which KIND of
 * words (heading / text / small print) and which way they line up. Everything
 * the old inspector offered - position, size, colour, weight - is decided by
 * the hand on the canvas or by step 2.
 */
function BlockSettings({
  region,
  label,
  onChange,
  onLabel,
  onPick,
  theme,
  onDelete,
  onRestack,
}: {
  region: SignageTemplateRegion | null;
  label: string;
  onChange: (next: SignageTemplateRegion) => void;
  onLabel: (field: string, value: string) => void;
  onPick: () => void;
  theme: SignageTemplateTheme;
  onDelete: (id: string) => void;
  onRestack: (id: string, to: "front" | "back") => void;
}) {
  if (!region) {
    return (
      <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
        Click a block on the left to say whether it is the same on every slide,
        or filled in each time. Add new blocks from the bar above the canvas.
      </Typography>
    );
  }

  const arrange = (
    <Stack direction="row" gap={1} sx={{ mt: 1 }}>
      <Button size="small" onClick={() => onRestack(region.id, "front")}>
        To front
      </Button>
      <Button size="small" onClick={() => onRestack(region.id, "back")}>
        To back
      </Button>
      <Box sx={{ flex: 1 }} />
      <Button
        size="small"
        color="error"
        onClick={() => onDelete(region.id)}
        data-testid="block-delete"
      >
        Remove
      </Button>
    </Stack>
  );

  // A panel and a clock have nothing to fill in: one is a colour, the other
  // reads the time. Offering the choice would be offering a decision with one
  // answer.
  if (region.kind === "shape" || region.kind === "clock") {
    return (
      <Stack gap={1}>
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
          {region.kind === "clock"
            ? "This shows the time, in this location's own timezone."
            : "This is a coloured panel. Its colour comes from the previous step."}
        </Typography>
        {arrange}
      </Stack>
    );
  }

  const perSlide = region.binding.kind === "field";
  const field = perSlide ? (region.binding as { field: string }).field : "";

  return (
    <Stack gap={2}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={perSlide ? "field" : "fixed"}
        onChange={(_, v) => {
          if (!v) return;
          onChange({
            ...region,
            binding:
              v === "field"
                ? // The key is the block's own id, so it cannot be renamed and
                  // cannot collide - the two ways the old field list broke.
                  { kind: "field", field: region.id }
                : { kind: "fixed", value: "" },
          });
        }}
        fullWidth
      >
        <ToggleButton value="fixed">Same every time</ToggleButton>
        <ToggleButton value="field">Filled in per slide</ToggleButton>
      </ToggleButtonGroup>

      {perSlide && (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={Boolean(
                (region.binding as { unitEditable?: boolean }).unitEditable
              )}
              onChange={(e) =>
                onChange({
                  ...region,
                  binding: {
                    kind: "field",
                    field,
                    ...(e.target.checked ? { unitEditable: true } : {}),
                  },
                })
              }
              inputProps={
                {
                  "data-testid": "block-unit-editable",
                } as React.InputHTMLAttributes<HTMLInputElement>
              }
            />
          }
          label={
            <Typography sx={{ fontSize: 13 }}>
              Unit staff can change this
            </Typography>
          }
        />
      )}
      {perSlide ? (
        <TextField
          size="small"
          label="What to call it"
          placeholder={
            region.kind === "image"
              ? "Picture"
              : region.kind === "slideshow"
                ? "Pictures"
                : region.kind === "list"
                  ? "The list"
                  : region.kind === "web"
                    ? webName(region)
                    : "Headline"
          }
          value={label}
          onChange={(e) => onLabel(field, e.target.value)}
          inputProps={{ "data-testid": "block-label" }}
          helperText="What the person filling this in will be asked for."
        />
      ) : region.kind === "image" ? (
        <Button size="small" onClick={onPick}>
          {region.binding.kind === "fixed" && region.binding.value
            ? "Change picture"
            : "Choose picture"}
        </Button>
      ) : region.kind === "slideshow" ? (
        <ImageListControl
          label="Pictures"
          value={region.binding.kind === "fixed" ? region.binding.value : ""}
          onChange={(value) =>
            onChange({ ...region, binding: { kind: "fixed", value } })
          }
          onPick={onPick}
        />
      ) : region.kind === "web" ? (
        <WebAddress
          region={region}
          onChange={(value) =>
            onChange({ ...region, binding: { kind: "fixed", value } })
          }
        />
      ) : (
        <TextField
          size="small"
          label={region.kind === "qr" ? "Address" : "Words"}
          placeholder={region.kind === "qr" ? "https://…" : undefined}
          value={region.binding.kind === "fixed" ? region.binding.value : ""}
          onChange={(e) =>
            onChange({
              ...region,
              binding: { kind: "fixed", value: e.target.value },
            })
          }
          multiline={region.kind === "text" || region.kind === "list"}
          minRows={region.kind === "list" ? 4 : undefined}
          helperText={
            region.kind === "list"
              ? region.table
                ? "One row per line, with | between the columns: Mon | Fish | Pudding"
                : "One line each. Start with a time and a | to line the times up: 12:00 | Lunch"
              : undefined
          }
          inputProps={{ "data-testid": "block-fixed" }}
        />
      )}

      {(region.kind === "text" || region.kind === "list") && (
        <TextLook region={region} theme={theme} onChange={onChange} />
      )}
      {region.kind === "web" && <WebLook region={region} onChange={onChange} />}
      {region.kind === "slideshow" && (
        <TextField
          select
          size="small"
          label="Each picture shows for"
          value={region.intervalSeconds ?? 8}
          onChange={(e) =>
            onChange({ ...region, intervalSeconds: Number(e.target.value) })
          }
        >
          {[5, 8, 15, 30, 60].map((n) => (
            <MenuItem key={n} value={n}>
              {n} seconds
            </MenuItem>
          ))}
        </TextField>
      )}
      {region.kind === "list" && (
        <Stack>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={Boolean(region.table)}
                onChange={(e) => {
                  const next = { ...region };
                  if (e.target.checked) next.table = true;
                  else {
                    delete next.table;
                    delete next.tableHeader;
                  }
                  onChange(next);
                }}
                inputProps={
                  {
                    "data-testid": "block-table",
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
            label={
              <Typography sx={{ fontSize: 13 }}>
                Show as a table (every | is a new column)
              </Typography>
            }
          />
          {region.table && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={Boolean(region.tableHeader)}
                  onChange={(e) => {
                    const next = { ...region };
                    if (e.target.checked) next.tableHeader = true;
                    else delete next.tableHeader;
                    onChange(next);
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13 }}>
                  First line is a heading row
                </Typography>
              }
            />
          )}
        </Stack>
      )}

      {arrange}
    </Stack>
  );
}

/**
 * Which kind of words, and which way they line up. Both chosen by NAME: the
 * size, weight and colour come from the theme, so a heading here always looks
 * like every other heading in the template.
 */
function TextLook({
  region,
  theme,
  onChange,
}: {
  region: SignageTemplateRegion;
  theme: SignageTemplateTheme;
  onChange: (next: SignageTemplateRegion) => void;
}) {
  const role = region.role ?? "body";
  return (
    <Stack gap={1}>
      <ToggleButtonGroup
        exclusive
        size="small"
        fullWidth
        value={role}
        aria-label="Kind of text"
        onChange={(_, v: SignageRegionRole | null) => {
          if (!v) return;
          onChange({
            ...region,
            role: v,
            style: { ...region.style, ...styleForRole(v, theme) },
          });
        }}
      >
        <ToggleButton value="title">Heading</ToggleButton>
        <ToggleButton value="body">Text</ToggleButton>
        <ToggleButton value="small">Small print</ToggleButton>
      </ToggleButtonGroup>
      {region.kind === "text" && (
        <ToggleButtonGroup
          exclusive
          size="small"
          fullWidth
          value={region.style?.align ?? "left"}
          aria-label="Line up"
          onChange={(_, v: "left" | "center" | "right" | null) => {
            if (!v) return;
            onChange({ ...region, style: { ...region.style, align: v } });
          }}
        >
          <ToggleButton value="left">Left</ToggleButton>
          <ToggleButton value="center">Centre</ToggleButton>
          <ToggleButton value="right">Right</ToggleButton>
        </ToggleButtonGroup>
      )}
    </Stack>
  );
}

/** The two things a web page in a box actually needs: its size, and how often it reloads. */
function WebLook({
  region,
  onChange,
}: {
  region: SignageTemplateRegion;
  onChange: (next: SignageTemplateRegion) => void;
}) {
  const zoom = region.style?.zoom ?? 1;
  return (
    <Stack gap={1.5}>
      <Box>
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          Page size: {Math.round(zoom * 100)}%
        </Typography>
        <Slider
          size="small"
          min={0.5}
          max={2.5}
          step={0.1}
          value={zoom}
          aria-label="Page size"
          onChange={(_, v) =>
            onChange({
              ...region,
              style: { ...region.style, zoom: Number(v) },
            })
          }
        />
        <Typography sx={{ fontSize: 11, color: tokens.inkMuted }}>
          Most web pages are made for a laptop. Bigger reads better from across
          a room.
        </Typography>
      </Box>
      <TextField
        select
        size="small"
        label="Reload the page"
        value={region.refreshMinutes ?? 0}
        onChange={(e) => {
          const minutes = Number(e.target.value);
          const next = { ...region };
          if (minutes > 0) next.refreshMinutes = minutes;
          else delete next.refreshMinutes;
          onChange(next);
        }}
      >
        <MenuItem value={0}>Never (the page updates itself)</MenuItem>
        <MenuItem value={5}>Every 5 minutes</MenuItem>
        <MenuItem value={15}>Every 15 minutes</MenuItem>
        <MenuItem value={60}>Every hour</MenuItem>
        <MenuItem value={360}>Every 6 hours</MenuItem>
      </TextField>
      <Alert severity="info" sx={{ fontSize: 12 }}>
        Shown on Foyer screens only. Screens still running the old signage
        player skip this slide. Videos inside the page may not play on Samsung
        screens.
      </Alert>
    </Stack>
  );
}

/**
 * The address of a web block, checked as it is typed.
 *
 * Same probe and the same wording as the full-screen web page item
 * (`SignagePlaylistDialog`'s `WebPageDialog`): a page that refuses to be framed
 * is a WHITE box on the wall with no error anywhere, so this is the only moment
 * the answer can be acted on. It advises and never blocks.
 */
function WebAddress({
  region,
  onChange,
}: {
  region: SignageTemplateRegion;
  onChange: (value: string) => void;
}) {
  const value = region.binding.kind === "fixed" ? region.binding.value : "";
  const [probe, setProbe] = useState<SignageUrlProbe | null>(null);
  const [checking, setChecking] = useState(false);
  const [rejected, setRejected] = useState(false);

  useEffect(() => {
    setProbe(null);
    setRejected(false);
  }, [region.id]);

  const check = async (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    setChecking(true);
    setProbe(null);
    setRejected(false);
    try {
      setProbe(await foyerApi.probeSignageUrl(trimmed));
    } catch {
      setRejected(true);
    } finally {
      setChecking(false);
    }
  };

  return (
    <Stack gap={1}>
      <TextField
        size="small"
        label={
          region.source === "instagram"
            ? "Elfsight share link or install code"
            : region.source === "vilmer"
              ? "VilMer board address"
              : "Web address"
        }
        placeholder={
          region.source === "instagram"
            ? "https://….elf.site, or paste the install code"
            : "https://…"
        }
        value={value}
        onChange={(e) => {
          // An Instagram block turns whatever Elfsight thing was pasted - the
          // install code, the widget id - into its share link on the spot, so
          // the preview shows the feed straight away.
          const raw = e.target.value;
          onChange(
            region.source === "instagram"
              ? (elfsightShareLink(raw) ?? raw)
              : raw
          );
        }}
        // On blur, not per keystroke: each check is a real request to
        // somebody else's server.
        onBlur={(e) => void check(e.target.value)}
        error={rejected}
        helperText={
          rejected
            ? "That is not an address a screen can be pointed at."
            : region.source === "instagram"
              ? "Instagram has no link that shows a feed on its own, so we use an Elfsight Instagram Feed widget. In Elfsight: open the widget → Install → Share Link → Copy, and paste it here. The install code works too."
              : region.source === "vilmer"
                ? "The address of this unit's VilMer activity board."
                : undefined
        }
        inputProps={{ "data-testid": "web-address" }}
      />
      {region.source === "instagram" && (
        <>
          {elfsightShareLink(value) && (
            <Typography
              sx={{ fontSize: 12, color: tokens.success }}
              data-testid="elfsight-recognised"
            >
              Elfsight widget recognised.
            </Typography>
          )}
          <Alert severity="warning" sx={{ fontSize: 12 }}>
            {ELFSIGHT_VIEW_NOTE}
          </Alert>
        </>
      )}
      {checking && (
        <Stack direction="row" alignItems="center" gap={1}>
          <CircularProgress size={14} />
          <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
            Checking the page…
          </Typography>
        </Stack>
      )}
      {probe?.embeddable === "no" && (
        <Alert severity="warning" sx={{ fontSize: 12 }}>
          This site refuses to be shown inside another page
          {probe.reason ? ` (${probe.reason})` : ""}. On a screen it would
          appear as a blank white box. Use a page that allows embedding.
        </Alert>
      )}
      {probe?.embeddable === "unknown" && (
        <Alert severity="info" sx={{ fontSize: 12 }}>
          We could not reach this page from here, so we cannot say whether it
          will appear. Check it on a screen before relying on it.
        </Alert>
      )}
      {probe?.embeddable === "yes" && (
        <Alert severity="success" sx={{ fontSize: 12 }}>
          This page can be shown on a screen.
        </Alert>
      )}
      {probe?.insecure && (
        <Alert severity="warning" sx={{ fontSize: 12 }}>
          This is an <code>http://</code> address. Screens will block it as
          insecure — use https if the site offers it.
        </Alert>
      )}
    </Stack>
  );
}

/** Nothing on the wall yet: an unpicked picture, an unset address. */
function isEmpty(region: SignageTemplateRegion): boolean {
  if (region.kind === "shape" || region.kind === "clock") return false;
  if (region.binding.kind === "field") {
    // A per-slide text block shows sample words in the preview; pictures,
    // pages and codes have no sample, so they need the label.
    return (
      region.kind === "image" ||
      region.kind === "web" ||
      region.kind === "qr" ||
      region.kind === "slideshow"
    );
  }
  return !region.binding.value.trim();
}

function placeholderFor(region: SignageTemplateRegion): string {
  const perSlide = region.binding.kind === "field";
  switch (region.kind) {
    case "image":
      return perSlide ? "Picture - chosen per slide" : "Picture - choose one";
    case "qr":
      return perSlide ? "QR code - link per slide" : "QR code - add a link";
    case "slideshow":
      return perSlide
        ? "Image rotation - pictures per slide"
        : "Image rotation - add pictures";
    case "web":
      return `${webName(region)} - ${
        perSlide ? "address per slide" : "add the address"
      }`;
    default:
      return "Empty";
  }
}

/** What the designer and the filler call a web block. */
function webName(region: SignageTemplateRegion): string {
  return region.source === "instagram"
    ? "Instagram"
    : region.source === "vilmer"
      ? "VilMer activities"
      : "Web page";
}

/**
 * The widgets, as one row of buttons above the canvas.
 *
 * Click to add, not drag from the bar: a click-added block lands centred and
 * selected, ready to move - one gesture fewer, and it works with a keyboard.
 */
function WidgetBar({
  canAddWeb,
  onAdd,
}: {
  canAddWeb: boolean;
  onAdd: (widget: SignageWidgetId) => void;
}) {
  const isWeb = (id: SignageWidgetId) =>
    id === "webpage" || id === "instagram" || id === "vilmer";
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={0.75}
      sx={{ mb: 1.5 }}
      data-testid="widget-bar"
    >
      {SIGNAGE_WIDGETS.map((w) => {
        const blocked = isWeb(w.id) && !canAddWeb;
        const button = (
          <span key={w.id}>
            <Button
              size="small"
              variant="outlined"
              disabled={blocked}
              onClick={() => onAdd(w.id)}
              data-testid={`add-${w.id}`}
              sx={{ textTransform: "none", fontSize: 12, py: 0.25 }}
            >
              + {w.label}
            </Button>
          </span>
        );
        return (
          <Tooltip
            key={w.id}
            title={
              blocked
                ? `At most ${MAX_WEB_REGIONS} web blocks per template - each one is a whole web page running on the screen.`
                : w.hint
            }
          >
            {button}
          </Tooltip>
        );
      })}
    </Stack>
  );
}

/**
 * The canvas: the real renderer underneath, block outlines on top.
 *
 * The design is drawn by `TemplateSlide` - the same component the wall runs -
 * so what a designer sees is what appears. The interactive layer sits ABOVE it
 * rather than inside, which is also the only option: in production the preview
 * lane is a different origin and cannot be hit-tested into.
 */
function Canvas({
  canvas,
  theme,
  regions,
  preview,
  selected,
  interactive,
  onSelect,
  onMove,
  onDelete,
}: {
  canvas: { width: number; height: number };
  theme: SignageTemplateTheme;
  regions: SignageTemplateRegion[];
  preview: Parameters<typeof TemplateSlide>[0]["slide"];
  selected: string | null;
  interactive: boolean;
  onSelect: (id: string | null) => void;
  onMove: (id: string, rect: SignageTemplateRegion["rect"]) => void;
  onDelete: (id: string) => void;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{
    id: string;
    mode: "move" | "resize";
    startX: number;
    startY: number;
    rect: SignageTemplateRegion["rect"];
  } | null>(null);

  return (
    <Box
      ref={boxRef}
      data-testid="template-canvas"
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={(e) => {
        if (!interactive || !selected) return;
        if (e.key === "Delete" || e.key === "Backspace") {
          e.preventDefault();
          onDelete(selected);
        }
      }}
      onClick={() => interactive && onSelect(null)}
      onPointerMove={(e) => {
        const d = drag.current;
        const box = boxRef.current?.getBoundingClientRect();
        if (!d || !box || box.width === 0) return;
        const scale = box.width / canvas.width;
        const dx = (e.clientX - d.startX) / scale;
        const dy = (e.clientY - d.startY) / scale;
        if (d.mode === "resize") {
          // From the bottom-right corner. Never smaller than one grid step
          // (a block too small to grab is a block that is lost), never past
          // the canvas edge.
          onMove(d.id, {
            ...d.rect,
            w: Math.max(
              GRID * 2,
              Math.min(canvas.width - d.rect.x, snap(d.rect.w + dx))
            ),
            h: Math.max(
              GRID * 2,
              Math.min(canvas.height - d.rect.y, snap(d.rect.h + dy))
            ),
          });
          return;
        }
        onMove(d.id, {
          ...d.rect,
          // Clamped inside the canvas: a block cannot be dragged out of
          // existence and then hunted for.
          x: Math.max(
            0,
            Math.min(canvas.width - d.rect.w, snap(d.rect.x + dx))
          ),
          y: Math.max(
            0,
            Math.min(canvas.height - d.rect.h, snap(d.rect.y + dy))
          ),
        });
      }}
      onPointerUp={() => (drag.current = null)}
      onPointerLeave={() => (drag.current = null)}
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: `${canvas.width} / ${canvas.height}`,
        border: `1px solid ${tokens.divider}`,
        borderRadius: "8px",
        overflow: "hidden",
        background: theme.background,
        userSelect: "none",
        touchAction: "none",
      }}
    >
      {/* The renderer scales to whatever box it is given, so this is a box. */}
      <Box sx={{ position: "absolute", inset: 0 }}>
        <TemplateSlide slide={preview} timezone="Europe/Stockholm" />
      </Box>

      {interactive &&
        regions.map((region) => (
          <Box
            key={region.id}
            data-testid={`region-${region.id}`}
            aria-label={`Block ${region.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(region.id);
            }}
            onPointerDown={(e) => {
              drag.current = {
                id: region.id,
                mode: "move",
                startX: e.clientX,
                startY: e.clientY,
                rect: region.rect,
              };
              (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
              onSelect(region.id);
            }}
            sx={{
              position: "absolute",
              left: `${(region.rect.x / canvas.width) * 100}%`,
              top: `${(region.rect.y / canvas.height) * 100}%`,
              width: `${(region.rect.w / canvas.width) * 100}%`,
              height: `${(region.rect.h / canvas.height) * 100}%`,
              border:
                region.id === selected
                  ? `2px solid ${tokens.brand}`
                  : isEmpty(region)
                    ? "2px dashed rgba(255,255,255,0.5)"
                    : "2px solid transparent",
              // The selected block's corner handle sits on its edge.
              overflow: "visible",
              cursor: "move",
              "&:hover": { border: `2px dashed ${tokens.brand}` },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* A block with nothing to draw yet still has to be SEEN, or the
                drop looks like it failed. Editor-only; the wall draws nothing. */}
            {region.binding.kind === "field" && region.binding.unitEditable && (
              <Box
                sx={{
                  position: "absolute",
                  top: 4,
                  left: 4,
                  fontSize: 11,
                  color: "#fff",
                  background: tokens.brand,
                  px: 0.75,
                  borderRadius: "4px",
                  pointerEvents: "none",
                }}
              >
                Unit
              </Box>
            )}
            {isEmpty(region) && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#fff",
                  background: "rgba(0,0,0,0.55)",
                  px: 1,
                  py: 0.25,
                  borderRadius: "4px",
                  pointerEvents: "none",
                }}
              >
                {placeholderFor(region)}
              </Typography>
            )}
            {region.id === selected && (
              <Box
                aria-label="Resize"
                data-testid={`resize-${region.id}`}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  drag.current = {
                    id: region.id,
                    mode: "resize",
                    startX: e.clientX,
                    startY: e.clientY,
                    rect: region.rect,
                  };
                  (e.currentTarget as HTMLElement).setPointerCapture?.(
                    e.pointerId
                  );
                }}
                sx={{
                  position: "absolute",
                  right: -6,
                  bottom: -6,
                  width: 14,
                  height: 14,
                  background: tokens.brand,
                  border: "2px solid #fff",
                  borderRadius: "3px",
                  cursor: "nwse-resize",
                }}
              />
            )}
          </Box>
        ))}
    </Box>
  );
}

/** A swatch and a hex box - the pairing the Custom screen editor established. */
function Colour({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Box
        component="input"
        type="color"
        aria-label={label}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          padding: 0,
          border: `1px solid ${tokens.divider}`,
          borderRadius: "8px",
          background: "none",
        }}
      />
      <Typography sx={{ fontSize: 14 }}>{label}</Typography>
    </Stack>
  );
}
